import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Clock, Plus, Trash2, Loader2, Users, MessageSquare, BarChart3, ShoppingBag, Store, MapPin, Calendar, Shield } from "lucide-react";
import { DAYS, DAY_LABELS } from "./adminShared";

/* ============ SETTINGS PANEL ============ */
export default function SettingsPanel({ settings, onSave }) {
  const [form, setForm] = useState(settings || {});
  const [saving, setSaving] = useState(false);
  const [rules, setRules] = useState([]);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  useEffect(() => {
    api.get("/admin/rules").then(res => setRules(res.data.rules || [])).catch(() => {});
  }, []);

  const handleSave = async () => {
    // VAT rates are governed: a change requires a written reason (audited).
    const vatChanged = rules.filter(r => r.name.startsWith("vat_rate_") &&
      form[r.name] !== undefined && String(form[r.name]) !== String(r.value));
    let payload = form;
    if (vatChanged.length) {
      const reason = window.prompt(
        `Du ändrar momssats (${vatChanged.map(r => r.label).join(", ")}).\nAnge anledning (obligatoriskt, loggas i händelseloggen):`);
      if (!reason || reason.trim().length < 5) { toast.error("Momsändring avbruten — anledning krävs"); return; }
      payload = { ...form, vat_change_reason: reason.trim() };
    }
    setSaving(true);
    try {
      const res = await api.put("/admin/settings", payload);
      onSave(res.data);
      toast.success("Inställningar sparade");
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte spara"); }
    finally { setSaving(false); }
  };

  const updateHours = (day, field, value) => {
    setForm(f => ({
      ...f,
      opening_hours: {
        ...f.opening_hours,
        [day]: { ...f.opening_hours?.[day], [field]: value }
      }
    }));
  };

  // Open/close and mode look like buttons, so they must take effect
  // immediately — not sit unsaved in the form until "Spara".
  const saveStatusNow = async (fields) => {
    setForm(f => ({ ...f, ...fields }));
    try {
      const res = await api.put("/admin/settings", fields);
      onSave(res.data);
      toast.success("Sparat");
    } catch (e) { toast.error("Kunde inte spara"); }
  };

  if (!form) return null;

  return (
    <div className="max-w-2xl" data-testid="settings-panel">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-playfair text-xl font-bold text-[#EDEDED]">Restauranginställningar</h2>
          <p className="text-[#E8E8EA] text-xs mt-1">Konfigurera restauranguppgifter</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#D4B483] text-[#0A0A0A] hover:bg-[#E5CFA5] font-semibold">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
        </Button>
      </div>

      <div className="space-y-5">
        {/* General Info */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <Store className="w-4 h-4 text-[#D4B483]" /> Allmänt
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Restaurangnamn</Label>
              <Input value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Telefon</Label>
              <Input value={form.phone || ""} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                placeholder="+46..." className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" />
            </div>
          </div>
          <div>
            <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Adress</Label>
            <Input value={form.address || ""} onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" />
          </div>
        </div>

        {/* Restaurant Status */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <Store className="w-4 h-4 text-[#D4B483]" /> Restaurangstatus
          </h3>
          <div className="space-y-4">
            {/* Manual vs Scheduled Mode Toggle */}
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
              <div>
                <p className="text-sm text-[#EDEDED] font-medium">{form.manual_mode ? "Manuellt läge" : "Schemalagt läge"}</p>
                <p className="text-xs text-[#E8E8EA]">{form.manual_mode ? "Öppna och stäng själv" : "Öppnar och stänger automatiskt enligt öppettiderna nedan"}</p>
              </div>
              <button
                onClick={() => saveStatusNow({ manual_mode: !form.manual_mode })}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  form.manual_mode
                    ? "bg-[#D4B483]/15 text-[#D4B483]"
                    : "bg-[#3B82F6]/15 text-[#3B82F6]"
                }`}
              >
                {form.manual_mode ? "Byt till schemalagt" : "Byt till manuellt"}
              </button>
            </div>

            {/* Manual Mode: Open/Close Button */}
            {form.manual_mode && (
              <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A]">
                <p className="text-xs text-[#E8E8EA] mb-3 font-semibold uppercase tracking-wide">Nuvarande status</p>
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full animate-pulse ${
                    form.is_open ? "bg-[#10B981]" : "bg-[#EF4444]"
                  }`} />
                  <span className="text-sm text-[#EDEDED] font-semibold">
                    {form.is_open ? "🟢 Restaurangen är ÖPPEN" : "🔴 Restaurangen är STÄNGD"}
                  </span>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    onClick={() => saveStatusNow({ is_open: true })}
                    className={`flex-1 h-10 font-semibold transition-all ${
                      form.is_open
                        ? "bg-[#10B981] text-white hover:bg-[#059669]"
                        : "bg-[#10B981]/20 text-[#10B981] hover:bg-[#10B981]/30"
                    }`}
                  >
                    Öppna restaurang
                  </Button>
                  <Button
                    onClick={() => saveStatusNow({ is_open: false })}
                    className={`flex-1 h-10 font-semibold transition-all ${
                      !form.is_open
                        ? "bg-[#EF4444] text-white hover:bg-[#DC2626]"
                        : "bg-[#EF4444]/20 text-[#EF4444] hover:bg-[#EF4444]/30"
                    }`}
                  >
                    Stäng restaurang
                  </Button>
                </div>
              </div>
            )}

            {/* Scheduled Mode: hours are set per day in the "Öppettider" section below */}
            {!form.manual_mode && (
              <div className="p-4 bg-[#0A0A0A] rounded-lg border border-[#27272A] flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#D4B483] mt-0.5 flex-shrink-0" />
                <p className="text-xs text-[#E8E8EA] leading-relaxed">
                  Restaurangen öppnar och stänger automatiskt enligt <span className="text-[#EDEDED] font-medium">Öppettider</span> nedan. Ställ in tider för varje veckodag där – utanför dem kan kunderna inte beställa.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Order Settings */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <ShoppingBag className="w-4 h-4 text-[#D4B483]" /> Beställningar
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Leveransavgift (kr)</Label>
              <Input type="number" value={form.delivery_fee ?? ""} onChange={e => setForm(f => ({ ...f, delivery_fee: parseInt(e.target.value) || 0 }))}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Max körtid leverans (min)</Label>
              <Input type="number" value={form.max_delivery_drive_minutes ?? 10} onChange={e => setForm(f => ({ ...f, max_delivery_drive_minutes: parseInt(e.target.value) || 10 }))}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" min="1" max="120" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Minsta orderbelopp (kr)</Label>
              <Input type="number" value={form.min_order_amount ?? ""} onChange={e => setForm(f => ({ ...f, min_order_amount: parseInt(e.target.value) || 0 }))}
                className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" />
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
            <div>
              <p className="text-sm text-[#EDEDED] font-medium">Ta emot beställningar</p>
              <p className="text-xs text-[#E8E8EA]">Pausa eller återuppta beställningar</p>
            </div>
            <Switch checked={form.accepting_orders} onCheckedChange={(v) => setForm(f => ({ ...f, accepting_orders: v }))} />
          </div>
        </div>

        {/* Business rules (Regler) — generated from the backend rules registry */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <Shield className="w-4 h-4 text-[#D4B483]" /> Regler
          </h3>
          <p className="text-xs text-[#B4B4BC]">Affärsregler som styr hur systemet agerar automatiskt. Sparas med Spara-knappen.</p>
          {rules.length === 0 ? (
            <p className="text-xs text-[#9A9AA2]">Laddar regler…</p>
          ) : (
            Object.entries(rules.reduce((g, r) => { (g[r.group] = g[r.group] || []).push(r); return g; }, {})).map(([group, list]) => (
              <div key={group}>
                <p className="text-[10px] uppercase tracking-wider text-[#9A9AA2] mb-2">{group}</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {list.map(r => (
                    <div key={r.name}>
                      <Label className="text-[#E8E8EA] text-xs mb-1.5 block">{r.label}</Label>
                      <Input
                        type={r.type === "number" ? "number" : "text"}
                        step={r.type === "number" ? "any" : undefined}
                        min={r.min} max={r.max}
                        value={form[r.name] ?? r.value}
                        onChange={e => setForm(f => ({ ...f, [r.name]: r.type === "number" ? (parseFloat(e.target.value) || 0) : e.target.value }))}
                        className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" />
                      <p className="text-[10px] text-[#9A9AA2] mt-1">{r.description}</p>
                      {r.temporary && r.expires && (
                        <p className="text-[10px] text-[#F59E0B] mt-1">Tillfällig sats — upphör {r.expires}, därefter {r.legal_rate !== undefined ? "lagstadgad sats gäller automatiskt" : ""}</p>
                      )}
                      {r.override_active && (
                        <p className="text-[10px] text-[#FCA5A5] mt-1">Manuell avvikelse från lagstadgad sats ({r.legal_rate} %)</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Opening Hours */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#D4B483]" /> Öppettider
          </h3>
          <div className="space-y-2">
            {DAYS.map(day => {
              const hrs = form.opening_hours?.[day] || { open: "11:00", close: "21:00", closed: false };
              return (
                <div key={day} className="flex items-center gap-3 p-2 rounded-lg bg-[#0A0A0A]">
                  <span className="text-sm text-[#EDEDED] w-20 shrink-0">{DAY_LABELS[day]}</span>
                  <div className="flex items-center gap-2 flex-1">
                    {hrs.closed ? (
                      <span className="text-xs text-[#EF4444] font-medium">Stängt</span>
                    ) : (
                      <>
                        <Input type="time" value={hrs.open} onChange={e => updateHours(day, "open", e.target.value)}
                          className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-8 w-24 text-xs" />
                        <span className="text-[#E8E8EA] text-xs">–</span>
                        <Input type="time" value={hrs.close} onChange={e => updateHours(day, "close", e.target.value)}
                          className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-8 w-24 text-xs" />
                      </>
                    )}
                  </div>
                  <button onClick={() => updateHours(day, "closed", !hrs.closed)}
                    className={`text-xs px-2 py-1 rounded-full font-medium transition-all ${hrs.closed ? "bg-[#EF4444]/15 text-[#EF4444]" : "bg-[#10B981]/15 text-[#10B981]"}`}>
                    {hrs.closed ? "Stängt" : "Öppet"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        {/* Pickup/Delivery Options */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#D4B483]" /> Leveransalternativ
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
              <div>
                <p className="text-sm text-[#EDEDED] font-medium">Acceptera avhämtning</p>
                <p className="text-xs text-[#E8E8EA]">Tillåt kunder att hämta beställningar</p>
              </div>
              <Switch checked={form.accepting_pickup ?? true} onCheckedChange={(v) => setForm(f => ({ ...f, accepting_pickup: v }))} />
            </div>
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
              <div>
                <p className="text-sm text-[#EDEDED] font-medium">Acceptera leverans</p>
                <p className="text-xs text-[#E8E8EA]">Tillåt kunder att begära leverans</p>
              </div>
              <Switch checked={form.accepting_delivery ?? true} onCheckedChange={(v) => setForm(f => ({ ...f, accepting_delivery: v }))} />
            </div>
          </div>
        </div>

        {/* Tax Settings */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#D4B483]" /> Skatt & ekonomi
          </h3>
          <div>
            <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Skattesats (%)</Label>
            <Input type="number" step="0.01" value={form.tax_rate ?? 0} onChange={e => setForm(f => ({ ...f, tax_rate: parseFloat(e.target.value) || 0 }))}
              className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" min="0" max="100" />
          </div>
        </div>

        {/* Table Management */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <Users className="w-4 h-4 text-[#D4B483]" /> Servering på plats
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
              <div>
                <p className="text-sm text-[#EDEDED] font-medium">Tillåt bokningar</p>
                <p className="text-xs text-[#E8E8EA]">Enable table reservations for dine-in guests</p>
              </div>
              <Switch checked={form.allow_table_reservations ?? false} onCheckedChange={(v) => setForm(f => ({ ...f, allow_table_reservations: v }))} />
            </div>
            {form.allow_table_reservations && (
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Max antal bord</Label>
                <Input type="number" value={form.max_tables ?? 10} onChange={e => setForm(f => ({ ...f, max_tables: parseInt(e.target.value) || 0 }))}
                  className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-11" min="1" />
              </div>
            )}
          </div>
        </div>

        {/* Notifications & Announcements */}
        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5 space-y-4">
          <h3 className="text-sm font-semibold text-[#EDEDED] flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-[#D4B483]" /> Meddelanden
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-[#0A0A0A] rounded-lg">
              <div>
                <p className="text-sm text-[#EDEDED] font-medium">Visa meddelande</p>
                <p className="text-xs text-[#E8E8EA]">Visa ett meddelande till kunder</p>
              </div>
              <Switch checked={form.enable_announcements ?? false} onCheckedChange={(v) => setForm(f => ({ ...f, enable_announcements: v }))} />
            </div>
            {form.enable_announcements && (
              <div>
                <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Meddelandetext</Label>
                <Textarea value={form.current_announcement || ""} onChange={e => setForm(f => ({ ...f, current_announcement: e.target.value }))}
                  className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-lg p-3 text-sm focus:outline-none focus:border-[#D4B483] resize-none h-24"
                  placeholder="Skriv meddelandetext..." />
              </div>
            )}

            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Om oss-text</Label>
              <Textarea value={form.about_us || ""} onChange={e => setForm(f => ({ ...f, about_us: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-lg p-3 text-sm focus:outline-none focus:border-[#D4B483] resize-none h-28"
                placeholder="Skriv texten som visas på Om oss-sidan..." />
            </div>
          </div>
        </div>

        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5">
          <h3 className="text-sm font-semibold text-[#EDEDED] mb-1">E-postavsändare</h3>
          <p className="text-[#A8ABB2] text-xs mb-4">Vilken avsändare kunderna ser. Du kan använda valfri adress @bästabiten.se – inget behöver skapas.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Avsändarnamn (visas i inkorgen)</Label>
              <Input value={form.email_sender_name || ""} onChange={e => setForm(f => ({ ...f, email_sender_name: e.target.value }))}
                placeholder="Bästa Biten" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Order & kvitton – från-adress</Label>
              <Input value={form.email_from_order || ""} onChange={e => setForm(f => ({ ...f, email_from_order: e.target.value }))}
                placeholder="order@bästabiten.se" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10" />
              <p className="text-[11px] text-[#A8ABB2] mt-1">Orderbekräftelse och "redo att hämtas"-mejl.</p>
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Konto & support – från-adress</Label>
              <Input value={form.email_from_service || ""} onChange={e => setForm(f => ({ ...f, email_from_service: e.target.value }))}
                placeholder="service@bästabiten.se" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10" />
              <p className="text-[11px] text-[#A8ABB2] mt-1">Lösenordsåterställning och e-postverifiering.</p>
            </div>
            <div className="pt-2 border-t border-[#1F1F22]">
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Svar-adress (dit kundernas svar kommer)</Label>
              <Input value={form.email_reply_to || ""} onChange={e => setForm(f => ({ ...f, email_reply_to: e.target.value }))}
                placeholder="din.gmail@gmail.com" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10" />
              <p className="text-[11px] text-[#A8ABB2] mt-1">När en kund trycker "Svara" på ett mejl hamnar det i den här inkorgen. Kan vara din vanliga Gmail – ingen ny brevlåda behövs.</p>
            </div>
          </div>
        </div>

        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5">
          <h3 className="text-sm font-semibold text-[#EDEDED] mb-1">Startsida – texter</h3>
          <p className="text-[#A8ABB2] text-xs mb-4">Lämna tomt för att använda standardtexten.</p>
          <div className="space-y-3">
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Rubrik (hjältesektion)</Label>
              <Input value={form.home_hero_title || ""} onChange={e => setForm(f => ({ ...f, home_hero_title: e.target.value }))}
                placeholder="Maten du ser fram emot hela dagen" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Underrubrik (hjältesektion)</Label>
              <Textarea value={form.home_hero_subtitle || ""} onChange={e => setForm(f => ({ ...f, home_hero_subtitle: e.target.value }))}
                className="w-full bg-[#0A0A0A] border border-[#27272A] text-[#EDEDED] rounded-lg p-3 text-sm focus:outline-none focus:border-[#D4B483] resize-none h-16"
                placeholder="Beställ snabbare, ät godare..." />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Rubrik – "Varför välja oss"</Label>
              <Input value={form.home_why_title || ""} onChange={e => setForm(f => ({ ...f, home_why_title: e.target.value }))}
                placeholder="En upplevelse, inte bara en måltid" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10" />
            </div>

            <div className="pt-2 border-t border-[#1F1F22]">
              <Label className="text-[#E8E8EA] text-xs mb-2 block">Fyra fördelar ("Varför välja oss")</Label>
              <div className="space-y-2.5">
                {[0,1,2,3].map(i => {
                  const hl = (form.home_highlights || [])[i] || { title: "", text: "" };
                  const setHl = (field, value) => setForm(f => {
                    const arr = [...(f.home_highlights || [])];
                    while (arr.length < 4) arr.push({ title: "", text: "" });
                    arr[i] = { ...arr[i], [field]: value };
                    return { ...f, home_highlights: arr };
                  });
                  return (
                    <div key={i} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 space-y-2">
                      <Input value={hl.title || ""} onChange={e => setHl("title", e.target.value)}
                        placeholder={`Rubrik ${i+1} (t.ex. Premiumsmaker)`} className="bg-[#121212] border-[#27272A] text-[#EDEDED] h-9 text-sm" />
                      <Textarea value={hl.text || ""} onChange={e => setHl("text", e.target.value)}
                        className="w-full bg-[#121212] border border-[#27272A] text-[#EDEDED] rounded-lg p-2.5 text-sm focus:outline-none focus:border-[#D4B483] resize-none h-14"
                        placeholder="Kort beskrivning..." />
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-2 border-t border-[#1F1F22]">
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Avslutande uppmaning – rubrik</Label>
              <Input value={form.home_cta_title || ""} onChange={e => setForm(f => ({ ...f, home_cta_title: e.target.value }))}
                placeholder="Hungrig redan nu?" className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10" />
            </div>
            <div>
              <Label className="text-[#E8E8EA] text-xs mb-1.5 block">Avslutande uppmaning – text</Label>
              <Input value={form.home_cta_text || ""} onChange={e => setForm(f => ({ ...f, home_cta_text: e.target.value }))}
                placeholder="Öppna menyn och lägg din beställning på under en minut." className="bg-[#0A0A0A] border-[#27272A] text-[#EDEDED] h-10" />
            </div>
          </div>
        </div>

        <div className="bg-[#121212] rounded-xl border border-[#27272A] p-5">
          <div className="flex items-center justify-between mb-1">
            <h3 className="text-sm font-semibold text-[#EDEDED]">Recensioner ("Vad kunderna säger")</h3>
            <Button type="button" size="sm" variant="outline"
              onClick={() => setForm(f => ({ ...f, home_reviews: [...(f.home_reviews || []), { name: "", rating: 5, text: "" }] }))}
              className="border-[#27272A] text-[#E8E8EA] hover:bg-[#1A1A1A] h-8 text-xs">
              <Plus className="w-3.5 h-3.5 mr-1" /> Lägg till
            </Button>
          </div>
          <p className="text-[#A8ABB2] text-xs mb-4">Lämna tomt för att visa standardexemplen.</p>
          <div className="space-y-3">
            {(form.home_reviews || []).map((rev, i) => (
              <div key={i} className="bg-[#0A0A0A] border border-[#27272A] rounded-lg p-3 space-y-2">
                <div className="flex gap-2 items-center">
                  <Input value={rev.name || ""} onChange={e => setForm(f => { const a = [...f.home_reviews]; a[i] = { ...a[i], name: e.target.value }; return { ...f, home_reviews: a }; })}
                    placeholder="Namn (t.ex. Sara M.)" className="bg-[#121212] border-[#27272A] text-[#EDEDED] flex-1 h-9 text-sm" />
                  <select value={rev.rating ?? 5} onChange={e => setForm(f => { const a = [...f.home_reviews]; a[i] = { ...a[i], rating: parseInt(e.target.value) }; return { ...f, home_reviews: a }; })}
                    className="bg-[#121212] border border-[#27272A] text-[#EDEDED] rounded-md h-9 text-sm px-2">
                    {[5,4,3,2,1].map(n => <option key={n} value={n}>{n} ★</option>)}
                  </select>
                  <button type="button" onClick={() => setForm(f => ({ ...f, home_reviews: f.home_reviews.filter((_, idx) => idx !== i) }))} className="text-[#A8ABB2] hover:text-[#EF4444]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <Textarea value={rev.text || ""} onChange={e => setForm(f => { const a = [...f.home_reviews]; a[i] = { ...a[i], text: e.target.value }; return { ...f, home_reviews: a }; })}
                  className="w-full bg-[#121212] border border-[#27272A] text-[#EDEDED] rounded-lg p-2.5 text-sm focus:outline-none focus:border-[#D4B483] resize-none h-16"
                  placeholder="Recensionstext..." />
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}
